/**
 * Route guards — §3.4.
 *
 * **Defence in depth, not the security boundary.** A user who hand-types `/ops`
 * sees a friendly 403 instead of a broken screen; a user who calls the ops API
 * directly gets a 403 from the server, which is the check that actually matters.
 * Never move an authorisation decision here from the backend.
 *
 * Three outcomes, and telling them apart is the point:
 *
 * * **loading** — `/me/` has not answered. Render nothing conclusive, or a
 *   signed-in user sees a login screen flash before their own page.
 * * **anonymous** — send them to log in, remembering where they were going.
 * * **wrong role** — a 403 page. Redirecting to login instead would loop a
 *   logged-in learner who typed `/teach` straight back to a portal they cannot
 *   enter.
 */
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, useLocation } from 'react-router'

import { Button, EmptyState, LoadingPanel } from '@/shared/ui'

import { loginPath } from './navigation'
import type { Role } from './roles'
import { useSession } from './useSession'

export function RequireAuth({ children }: { children?: React.ReactNode }) {
  const session = useSession()
  const location = useLocation()
  const { t } = useTranslation('common')

  if (session.status === 'loading') return <LoadingPanel label={t('state.loading')} />

  if (session.status === 'anonymous') {
    return <Navigate to={loginPath(location.pathname, location.search)} replace />
  }

  return children ?? <Outlet />
}

export function RequireRole({
  roles,
  children,
}: {
  roles: readonly Role[]
  /** Omit to guard a nested route tree via `<Outlet />`. */
  children?: React.ReactNode
}) {
  const session = useSession()
  const location = useLocation()
  const { t } = useTranslation('common')

  if (session.status === 'loading') return <LoadingPanel label={t('state.loading')} />

  if (session.status === 'anonymous') {
    return <Navigate to={loginPath(location.pathname, location.search)} replace />
  }

  if (!roles.some((role) => session.hasRole(role))) {
    return (
      <div role="alert">
        <EmptyState
          title={t('error.forbidden.title')}
          body={t('error.forbidden.body')}
          action={
            <Button variant="secondary" asChild>
              <a href="/">{t('nav.home')}</a>
            </Button>
          }
        />
      </div>
    )
  }

  return children ?? <Outlet />
}
